/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createAsyncThunk, createAction } from '@reduxjs/toolkit';
import _ from 'lodash';
import moment from 'moment-timezone';

import { ApiStatus } from '../../../ts';
import type { Action } from '../../../ts/action';
import type { AnalyticsFilters } from '../../../ts/analytics';
import { SkillType } from '../../../ts/skill';
import { CreateAsyncThunk } from '../../../ts/state';
import { getAllActions } from '../../actions/store/actions/selectors';
import {
  selectIsDialogEnabledInDraftEnvironment,
  selectDraftEnvironment
} from '../../ia/store/environments/selectors';
import { addErrorMessage } from '../../ia/store/messages/actions';
import {
  getActionSkillId,
  getAssistantId
} from '../../ia/store/session/selectors';
import { selectSkillLanguage } from '../../ia/store/skills/core-selectors';
import {
  getReports,
  getRequestsForGroup,
  getUnrecognizedRequests
} from '../api';

type FilterParams = {
  actionTitle?: string;
  assistantId: string;
  completionStatus?: string;
  containment?: string;
  conversationalSkillState: string;
  conversational_skill_state?: string;
  endDate?: string;
  filteredReasons?: any;
  intents?: any;
  interval?: string;
  keywords?: string;
  language: string;
  order?: string;
  noIntents?: boolean;
  pageAfterKey?: string;
  parentAssistantId: string;
  range?: string;
  recognition?: string;
  sampleSize?: number;
  search?: string;
  sortField?: string;
  startDate?: string;
  topics?: string[];
  entities?: string[];
  resolution_method?: string[];
  version?: string;
  responseType?: string;
};

//max logs returned per conversations logs api
export const MAX_LOGS = 100;
const getFilterParams = state => {
  const {
    range,
    startDate,
    endDate,
    interval,
    topics,
    environment,
    resolution_method
  } = state().analytics.filters;
  const language = selectSkillLanguage(state());
  const parentAssistantId = getAssistantId(state());
  const hasDialogSkill = selectIsDialogEnabledInDraftEnvironment(state());
  const params = {
    language: language,
    range: range,
    startDate: startDate,
    endDate: endDate,
    interval: interval,
    topics: topics,
    assistantId: environment,
    parentAssistantId,
    conversationalSkillState: hasDialogSkill ? 'coexistence' : 'action',
    version: 'v2',
    resolution_method: resolution_method
  };

  return params;
};

export const setFilterParams = createAction(
  'analytics/setFilterParams',
  params => {
    return {
      payload: params
    };
  }
);

export const setEnvironment = createAction(
  'analytics/setEnvironment',
  params => {
    return {
      payload: params
    };
  }
);

export const setConvoSearchResponseType = createAction(
  'analytics/setConvoSearchResponseType',
  params => {
    return {
      payload: params
    };
  }
);

export const clearAllFilters = createAction('analytics/clearAllFilters');

export const setPageConversation = createAction(
  'analytics/setPageConversation',
  params => {
    return {
      payload: params
    };
  }
);

export const setPageAllLogs = createAction(
  'analytics/setPageAllLogs',
  params => {
    return {
      payload: params
    };
  }
);

export const setPageAllConvoSearchLogs = createAction(
  'analytics/setPageAllConvoSearchLogs',
  params => {
    return {
      payload: params
    };
  }
);

export const setPageSingleAction = createAction(
  'analytics/setPageSingleAction',
  params => {
    return {
      payload: params
    };
  }
);

export const removeFilterParams = createAction(
  'analytics/setFilterParams',
  (allFilters, filter?) => {
    let filterObj = {};
    const updatedTopics = _.difference(allFilters.topics, filter?.topics);
    const updatedKeywords = _.difference(allFilters.keywords, filter?.keywords);
    const updatedIncompleteReasons = _.difference(
      allFilters.incompleteReasons,
      filter?.incompleteReasons
    );
    const updatedCompleteReasons = _.difference(
      allFilters.completeReasons,
      filter?.completeReasons
    );
    const updatedPercentageReasons = _.difference(
      allFilters.percentageReasons,
      filter?.percentageReasons
    );
    const updatedEntities = _.difference(allFilters.entities, filter?.entities);
    const updatedRecognitionTypes = _.difference(
      allFilters.resolution_method,
      filter?.resolution_method
    );
    filterObj = {
      ...allFilters,
      ...filter,
      topics: updatedTopics,
      keywords: updatedKeywords,
      entities: updatedEntities,
      resolution_method: updatedRecognitionTypes,
      incompleteReasons: updatedIncompleteReasons,
      completeReasons: updatedCompleteReasons,
      percentageReasons: updatedPercentageReasons
    };
    return {
      payload: filterObj
    };
  }
);

export const getAnalyticsReports = createAsyncThunk<
  {
    type: string;
    filters?: any;
    data: any;
  },
  string,
  CreateAsyncThunk
>(
  'analytics/fetchReports',
  async (type, { dispatch, getState }) => {
    try {
      const { filters } = getState().analytics;
      const isEngagement =
        type === 'totalActiveUsers' ||
        type === 'totalConversations' ||
        type === 'totalUserRequests';
      let params: FilterParams = getFilterParams(getState);

      const start = moment(filters.startDate);
      const end = moment(filters.endDate);

      const diff = end.diff(start, 'days');
      if (isEngagement) {
        if (diff > 7) {
          params = {
            ...params,
            interval: 'Week'
          };
        } else {
          params = {
            ...params,
            interval: 'Day'
          };
        }
      }

      if (type === 'skillHasData' || type === 'intentActionPairs') {
        params = {
          conversationalSkillState: 'coexistence',
          assistantId: params.assistantId,
          language: params.language,
          parentAssistantId: params.parentAssistantId
        };
      }

      const apiResult = await getReports(type, params);
      if (type === 'skillHasData' || type === 'intentActionPairs') {
        return {
          type: type,
          data: apiResult
        };
      }
      return {
        type: type,
        filters: isEngagement ? params : filters,
        data: {
          totals: apiResult.totals,
          reports: apiResult.results
        }
      };
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  },
  {
    condition: (type, { getState }) => {
      const { analytics } = getState();
      const stateObjMapping = {
        totalMessages: 'messages',
        totalConversations: 'conversations',
        totalActiveUsers: 'activeUsers',
        totalUserRequests: 'userRequests',
        coverage: 'coverage',
        containment: 'containment',
        actionCompletionHistogram: 'actionCompletionHistogram',
        recognition: 'recognition',
        skillHasData: 'skillHasData',
        intentActionPairs: 'intentActionPairs'
      };
      const mapValue = stateObjMapping[type];
      const fetchStatus = analytics[mapValue].loading;
      if (fetchStatus === 'fulfilled' || fetchStatus === 'loading') {
        // Already fetched or in progress, don't need to re-fetch
        return false;
      }
    }
  }
);

export const getSingleActionHistogram = createAsyncThunk<
  {
    type: string;
    filters: AnalyticsFilters;
    data: any;
  },
  { action: { id: string }; type: string },
  CreateAsyncThunk
>(
  'analytics/fetchSingleActionHistogram',
  async (options, { dispatch, getState }) => {
    try {
      const { action, type } = options;
      const params: FilterParams = getFilterParams(getState);
      const { filters } = getState().analytics;
      params.completionStatus = 'all';

      params.intents = [action.id];

      const apiResult = await getReports(
        'singleActionCompletionHistogram',
        params
      );

      return {
        type: type,
        filters: filters,
        data: {
          totals: apiResult.totals,
          reports: apiResult.results
        }
      };
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  }
);

export const getHistorgram = createAsyncThunk(
  'analytics/fetchHistogram',
  async (histogram, { dispatch, getState }) => {
    try {
      const params = getFilterParams(getState);
      const apiResult = await getReports(histogram, params);
      return { type: histogram, results: apiResult.results };
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  }
);

export const getSingleConversation = createAsyncThunk<
  {
    convoId: string;
    results: any;
  },
  string,
  CreateAsyncThunk
>(
  'analytics/fetchSingleConversation',
  async (convoId, { dispatch, getState }) => {
    try {
      const { environment } = getState().analytics.filters;
      const language = selectSkillLanguage(getState());
      const parentAssistantId = getAssistantId(getState());
      const params = {
        language: language,
        assistantId: environment,
        conversationId: convoId,
        parentAssistantId,
        version: 'v3.1'
      };

      const apiResult = await getReports('singleConversation', params);

      return { convoId: convoId, results: apiResult };
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  }
);

export const getGenerativeAiMetrics = createAsyncThunk<
  { reports: object },
  void,
  CreateAsyncThunk
>('analytics/fetchGenerativeAi', async (__, { dispatch, getState }) => {
  try {
    const params: FilterParams = getFilterParams(getState);

    const apiResult = await getReports('generativeAIMetrics', params);

    return { reports: apiResult.reports };
  } catch (error) {
    dispatch(addErrorMessage(error));
    throw error;
  }
});

export const getMessageLogs = createAsyncThunk<
  {
    logs: any;
    actions: Action[];
    requests: any;
    initialCall: boolean;
  },
  {
    actions: Action[];
    initialCall: boolean;
    next: boolean;
  },
  CreateAsyncThunk
>(
  'analytics/fetchMessageLogs',
  async (params, { dispatch, getState }) => {
    try {
      const {
        noIntents,
        topics,
        keywords,
        containment,
        recognition,
        search,
        sortOrder,
        resolution_method
      } = getState().analytics?.filters;
      const { page_after_key } = getState().analytics?.messageLogs?.pagination;
      const requests = getState().analytics?.messageLogs?.requests;
      const options: FilterParams = getFilterParams(getState);
      const allIntents = getState().models?.intents;
      const selectedIntents = [];
      //from the selected topics, find selected dialog intents
      if (!_.isEmpty(topics)) {
        _.forEach(topics, topic => {
          const intentFound = _.find(
            allIntents,
            intent => intent.intent === topic
          );
          if (intentFound) {
            selectedIntents.push(topic);
            delete topics[topic];
          }
        });
      }
      const selectedActions = _.difference(topics, selectedIntents);
      options.intents = selectedIntents;
      options.keywords = !_.isEmpty(keywords) ? keywords?.join('|') : null;
      options.containment = containment;
      options.recognition = recognition;
      options.search = search;
      options.noIntents = noIntents ? true : null;
      options.order = sortOrder;
      options.sampleSize = MAX_LOGS;
      options.topics = selectedActions;
      options.resolution_method = resolution_method;

      if (params.next) {
        options.pageAfterKey = page_after_key;
      }

      const logs = await getReports('conversationLogs', options);
      return {
        logs: logs,
        actions: params.actions,
        requests: params.initialCall ? [] : requests,
        initialCall: params.initialCall
      };
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  },
  {
    condition: (params, { getState }) => {
      const { analytics } = getState();
      if (
        params?.initialCall &&
        analytics?.messageLogs?.loading === ApiStatus.LOADING
      ) {
        return false;
      }
    }
  }
);

export const getconvoSearchMessageLogs = createAsyncThunk<
  {
    logs: any;
    actions: Action[];
    requests: any;
    initialCall: boolean;
  },
  {
    actions: Action[];
    initialCall: boolean;
    next?: boolean;
  },
  CreateAsyncThunk
>(
  'analytics/fetchconvoSearchMessageLogs',
  async (params, { dispatch, getState }) => {
    try {
      const { convoSearchResponseType } = getState().analytics?.filters;

      //Pagination
      const { page_after_key } =
        getState().analytics?.convoSearchMessageLogs?.pagination;

      const requests = getState().analytics?.convoSearchMessageLogs?.requests;

      const options: FilterParams = getFilterParams(getState);
      options.sampleSize = MAX_LOGS;
      options.responseType = convoSearchResponseType;

      if (params.next) {
        options.pageAfterKey = page_after_key;
      }

      const logs = await getReports('conversationalSearchLogs', options);
      return {
        logs: logs,
        actions: params.actions,
        requests: params.initialCall ? [] : requests,
        initialCall: params.initialCall
      };
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  },
  {
    condition: (params, { getState }) => {
      const { analytics } = getState();
      if (
        params?.initialCall &&
        analytics?.convoSearchMessageLogs?.loading === ApiStatus.LOADING
      ) {
        return false;
      }
    }
  }
);

export const getAllLogs = createAsyncThunk<
  {
    logs: any;
    actions: Action[];
    requests: any;
    initialCall: boolean;
  },
  {
    actions: Action[];
    initialCall: boolean;
    next: string;
  },
  CreateAsyncThunk
>(
  'analytics/fetchAllLogs',
  async (params, { dispatch, getState }) => {
    try {
      const {
        noIntents,
        topics,
        keywords,
        containment,
        recognition,
        search,
        sortOrder,
        entities,
        resolution_method
      } = getState().analytics?.filters;
      const { page_after_key } = getState().analytics?.allLogs?.pagination;
      const requests = getState().analytics?.allLogs?.requests;
      const options: FilterParams = getFilterParams(getState);
      const allIntents = getState().models?.intents;
      const selectedIntents = [];
      //from the selected topics, find selected dialog intents
      if (!_.isEmpty(topics)) {
        _.forEach(topics, topic => {
          const intentFound = _.find(
            allIntents,
            intent => intent.intent === topic
          );
          if (intentFound) {
            selectedIntents.push(topic);
            delete topics[topic];
          }
        });
      }
      const selectedActions = _.difference(topics, selectedIntents);
      options.intents = selectedIntents;
      options.keywords = !_.isEmpty(keywords) ? keywords?.join('|') : null;
      options.containment = containment;
      options.recognition = recognition;
      options.search = search;
      options.noIntents = noIntents ? true : null;
      options.order = sortOrder;
      options.sampleSize = MAX_LOGS;
      options.topics = selectedActions;
      options.entities = entities;
      options.resolution_method = resolution_method;

      if (params.next) {
        options.pageAfterKey = page_after_key;
      }
      const allLogs = await getReports('messageLogs', options);
      return {
        logs: allLogs,
        actions: params.actions,
        requests: params.initialCall ? [] : requests,
        initialCall: params.initialCall
      };
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  },
  {
    condition: (params, { getState }) => {
      const { analytics } = getState();
      if (
        params?.initialCall &&
        analytics?.allLogs?.loading === ApiStatus.LOADING
      ) {
        return false;
      }
    }
  }
);

export const getTopicsReport = createAsyncThunk<
  {
    actions: any;
    results: any;
    type: string;
  },
  {
    actions: any;
    type: string;
  },
  CreateAsyncThunk
>('analytics/fetchTopics', async (params, { dispatch, getState }) => {
  try {
    const options: FilterParams = getFilterParams(getState);
    const stateObjMapping = {
      mostPopular: 'topIntents',
      leastPopular: 'topIntents'
    };

    options.conversational_skill_state = 'coexistence';
    options.sampleSize = 5;
    options.order = 'desc';

    if (params.type === 'leastPopular') {
      options.order = 'asc';
    }

    const apiResult = await getReports(stateObjMapping[params.type], options);
    return {
      type: params.type,
      results: apiResult.results,
      actions: params.actions
    };
  } catch (error) {
    dispatch(addErrorMessage(error));
    throw error;
  }
});

export const getActionCompletion = createAsyncThunk(
  'analytics/fetchActionCompletion',
  async (
    params: { actions: any; isOverview: boolean; type: string },
    { dispatch, getState }
  ) => {
    const { isOverview, type, actions } = params;
    try {
      const options: FilterParams = getFilterParams(getState);
      if (type === 'frequentActions') {
        options.sampleSize = 5;
        options.sortField = 'most_and_least_frequent';
      } else if (type === 'actionCompletion' && isOverview) {
        options.sampleSize = 5;
        options.sortField = 'complete_percent';
        options.order = 'asc';
      }
      delete options.interval;

      const apiActions = await getReports('actionCompletionTable', options);
      return {
        results: apiActions.results,
        actions: actions,
        isOverview: isOverview
      };
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  }
);

export const getRecognitionClusters = createAsyncThunk<
  {},
  {
    isRefresh?: boolean;
  },
  CreateAsyncThunk
>(
  'analytics/fetchRecognitionClusters',
  async (isRefresh, { dispatch, getState }) => {
    try {
      const skillId = getActionSkillId(getState());
      const { environment } = getState().analytics.filters;
      const draftSkill = selectDraftEnvironment(getState());
      const skillReferenceId = draftSkill?.skill_references?.filter(
        skill => skill.type === SkillType.ACTION
      )[0]?.skill_id;
      const requests = await getUnrecognizedRequests({
        integrationId: environment,
        skill_reference_id: skillReferenceId,
        ...isRefresh,
        skillId: skillId
      });
      return requests.data;
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  }
);

export const getSingleUnrecognizedGroup = createAsyncThunk<
  {},
  { group: string },
  CreateAsyncThunk
>(
  'analytics/fetchSingleUnrecognizedGroup',
  async ({ group }, { dispatch, getState }) => {
    try {
      const skillId = getActionSkillId(getState());
      const actions = getAllActions(getState());
      const { environment } = getState().analytics.filters;
      const draftSkill = selectDraftEnvironment(getState());
      const skillReferenceId = draftSkill?.skill_references?.filter(
        skill => skill.type === SkillType.ACTION
      )[0]?.skill_id;
      const requests = await getRequestsForGroup({
        group,
        skillId: skillId,
        integrationId: environment,
        skill_reference_id: skillReferenceId
      });

      return {
        actions,
        ...requests.data
      };
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  }
);

export const getSingleActionRequests = createAsyncThunk<
  {
    data: any;
    initialCall: boolean;
    type: string;
    prevRequests: any;
  },
  {
    action: any;
    initialCall: boolean;
    next?: string;
    type: string;
  },
  CreateAsyncThunk
>('analytics/fetchSingleAction', async (params, { dispatch, getState }) => {
  try {
    const { action, type, initialCall, next } = params;
    const { incompleteReasons, completeReasons, percentageReasons } =
      getState().analytics?.filters;
    const {
      singleActionIncomplete,
      singleActionComplete,
      singleActionPercentage
    } = getState().analytics;
    const options: FilterParams = getFilterParams(getState);
    options.completionStatus = type === 'percentage' ? 'all' : type;
    options.actionTitle = action.title;
    options.intents = [action.id];
    if (!_.isEmpty(incompleteReasons) && params.type === 'incomplete') {
      const reasons = _.map(incompleteReasons, reason => {
        return reason.replace('Incomplete: ', '');
      });
      options.filteredReasons = reasons;
    } else if (!_.isEmpty(completeReasons) && params.type === 'complete') {
      const reasons = _.map(completeReasons, reason => {
        return reason.replace('Complete: ', '');
      });
      options.filteredReasons = reasons;
    } else if (!_.isEmpty(percentageReasons) && params.type === 'percentage') {
      const reasons = _.map(percentageReasons, reason => {
        if (reason.includes('Complete')) {
          return reason.replace('Complete: ', '');
        } else {
          return reason.replace('Incomplete: ', '');
        }
      });
      options.filteredReasons = reasons;
    }
    let key, prevData;
    switch (type) {
      case 'percentage':
        key = singleActionPercentage.pagination.page_after_key;
        prevData = singleActionPercentage.requests;
        break;
      case 'complete':
        key = singleActionComplete.pagination.page_after_key;
        prevData = singleActionComplete.requests;
        break;
      case 'incomplete':
        key = singleActionIncomplete.pagination.page_after_key;
        prevData = singleActionIncomplete.requests;
        break;
      default:
        break;
    }
    if (next) {
      options.pageAfterKey = key;
    }
    delete options.interval;
    delete options.range;
    const data = await getReports('singleActionCompletionTable', options);
    return {
      data: data,
      type: type,
      prevRequests: initialCall ? [] : prevData,
      initialCall: initialCall
    };
  } catch (error) {
    dispatch(addErrorMessage(error));
    throw error;
  }
});

export const clearRecognitionClusters = createAction(
  'analytics/clearRecognitionClusters'
);
